import { useState } from "react";
import { AlertTriangleIcon, CheckCircle2Icon, PencilIcon } from "lucide-react";
import { useAuth } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCampaignStore } from "@/stores";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface EditFieldDescriptionDialogProps {
  fieldName: string;
  currentDescription: string;
  allFields: Array<{
    name: string;
    type: string;
    description: string;
    negotiation: boolean;
  }>;
  campaignId: string;
}

export default function EditFieldDescriptionDialog({
  fieldName,
  currentDescription,
  allFields,
  campaignId,
}: EditFieldDescriptionDialogProps) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(currentDescription);
  const { getToken } = useAuth();
  const { updateCampaignField, updatingCampaignField } = useCampaignStore();

  const handleSave = async () => {
    const token = await getToken();
    if (!token) return;

    const updatedFields = allFields.map((f) =>
      f.name === fieldName ? { ...f, description } : f,
    );

    await updateCampaignField(
      "fields",
      JSON.stringify(updatedFields),
      campaignId,
      token,
    );

    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setDescription(currentDescription);
    }
    setOpen(isOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="opacity-0 group-hover/header:opacity-100 transition-none [&_svg]:size-3.5 [&_svg]:opacity-70 hover:[&_svg]:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setOpen(true);
            }}
          >
            <PencilIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Edit Field Description</TooltipContent>
      </Tooltip>

      <AlertDialogContent className="sm:max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Edit Description</AlertDialogTitle>
          <AlertDialogDescription>
            This helps Amy understand what information to collect from creators.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-2">
          <Alert variant="warning" className="mb-5">
            <AlertTitle>
              <AlertTriangleIcon />
              Changing the {fieldName}'s description mid conversation!
            </AlertTitle>
            <AlertDescription>
              Updating a field description mid-conversation changes the
              instructions for all creators in this campaign. Make changes
              carefully - they may affect what information Amy collects.
            </AlertDescription>
          </Alert>
          <Label htmlFor="field-description">Field Description</Label>
          <Textarea
            id="field-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Describe what this field collects..."
            className="min-h-24 shadow-xs focus-visible:ring-warning/20 focus-visible:border-warning"
            autoFocus
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="neutral"
            onClick={handleSave}
            disabled={updatingCampaignField || description === currentDescription}
          >
            <CheckCircle2Icon />
            Update Description
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
